
import { Type } from "class-transformer";
import { IsNotEmpty, IsString } from "class-validator";
import { isEqual } from "date-fns";
import i18n from "../i18n";
import { AbstractJSONModel } from "./master-model";
import { ISupervisionTimeDutiesPut, SupervisionTimeDuties } from "./supervisionTimeDuties";
import { dayOfWeekToTranslationKey } from "../modules/timeSchedule/utils/dutyUtils";
export interface ISupervisionTimeSchedule {
  id: number;
  name: string;
  created_at: Date;
  updated_at: Date;
  institution_id: number;
}

export interface ISupervisionTimeSchedulePost {
  create_supervision_time_schedule: {
    name: string;
    supervision_time_duties: SupervisionTimeDuties[];
  };
}

export interface ISupervisionTimeSchedulePut {
  update_supervision_time_schedule: {
    id: number;
    name: string;
    supervision_time_duties: ISupervisionTimeDutiesPut[];
  };
}

export class SupervisionTimeSchedule extends AbstractJSONModel implements ISupervisionTimeSchedule {
  public id = -1;
  @IsString()
  @IsNotEmpty({ message: i18n.global.t("timeSchedule.nameIsRequired").toString() })
  public name = "";
  public hours = 0;
  public institution_id = -1;
  @Type(() => Date)
  public created_at: Date = new Date();
  @Type(() => Date)
  public updated_at: Date = new Date();
  public supervision_time_duties: SupervisionTimeDuties[] = [];

  public updated(): boolean {
    return !isEqual(this.created_at, this.updated_at);
  }

  public getMaxOccurrences(): number {
    return this.supervision_time_duties.reduce((max, duty) => {
      return Math.max(max, duty.week_cycle_index);
    }, 0);
  }

  public getMaxOccurrencesByDayOfWeek(dayOfWeek: number): number {
    return this.supervision_time_duties
      .filter((duty) => duty.day_of_week === dayOfWeek)
      .reduce((max, duty) => {
        return Math.max(max, duty.week_cycle_index);
      }, 0);
  }

  public getWeekDays(): { week_cycle_index: number; day_of_week: number; day_name: string }[] {
    // Result must be Monday, Tuesday 1, Tuesday 2, Wednesday, Thursday, Friday
    // Use a Map to track unique combinations of day_of_week and week_cycle_index
    const uniqueDays = new Map<
      string,
      { week_cycle_index: number; day_of_week: number; day_name: string }
    >();

    // First, collect all unique day combinations from duties
    this.supervision_time_duties.forEach((duty) => {
      const key = `${duty.day_of_week}-${duty.week_cycle_index}`;
      if (!uniqueDays.has(key)) {
        uniqueDays.set(key, {
          week_cycle_index: duty.week_cycle_index,
          day_of_week: duty.day_of_week,
          day_name: "" // We'll set this after checking for multiple occurrences
        });
      }
    });

    // Ensure all weekdays (Monday-Friday) are included, even if they have no duties
    const weekdays = [1, 2, 3, 4, 5]; // Monday through Friday
    weekdays.forEach((dayOfWeek) => {
      const key = `${dayOfWeek}-1`; // Default to week_cycle_index = 1
      if (!uniqueDays.has(key)) {
        uniqueDays.set(key, {
          week_cycle_index: 1,
          day_of_week: dayOfWeek,
          day_name: ""
        });
      }
    });

    // Count occurrences per day of week to determine naming strategy
    const dayOfWeekCounts = new Map<number, number>();
    uniqueDays.forEach((day) => {
      const count = dayOfWeekCounts.get(day.day_of_week) || 0;
      dayOfWeekCounts.set(day.day_of_week, count + 1);
    });

    // Now set the day names based on whether there are multiple occurrences
    uniqueDays.forEach((day) => {
      const dayName = i18n.global.t(dayOfWeekToTranslationKey(day.day_of_week)).toString();
      const hasMultipleOccurrences = (dayOfWeekCounts.get(day.day_of_week) || 0) > 1;

      if (hasMultipleOccurrences) {
        day.day_name = `${dayName} ${day.week_cycle_index}`;
      } else {
        day.day_name = dayName;
      }
    });

    return Array.from(uniqueDays.values()).sort((a, b) => {
      // First sort by day_of_week, then by week_cycle_index
      if (a.day_of_week !== b.day_of_week) {
        return a.day_of_week - b.day_of_week;
      }
      return a.week_cycle_index - b.week_cycle_index;
    });
  }

  public convertToPostObject(duties: SupervisionTimeDuties[]): ISupervisionTimeSchedulePost {
    const s: ISupervisionTimeSchedulePost = Object.assign({
      create_supervision_time_schedule() {}
    });
    s.create_supervision_time_schedule = {
      name: this.name,
      supervision_time_duties: duties
    };
    return s;
  }

  public convertToPutObject(duties: SupervisionTimeDuties[]): ISupervisionTimeSchedulePut {
    const s: ISupervisionTimeSchedulePut = Object.assign({
      update_supervision_time_schedule() {}
    });
    s.update_supervision_time_schedule = {
      id: this.id,
      name: this.name,
      supervision_time_duties: duties.map((d) => d.convertToPutObject())
    };
    return s;
  }

  equals(other: ISupervisionTimeSchedule): boolean {
    return other.id === this.id;
  }
}
